import { toast } from 'sonner'
import { RETURN_TO_KEY, useSessionStore } from '@/stores/session'
import type {
  AccountResponse,
  ApiErrorBody,
  AuthCompleteResponse,
  AuthStartResponse,
  CaseCommentEntry,
  CaseDetail,
  CaseFileUploadResponse,
  CaseListFilter,
  CaseListScope,
  CaseParticipantsResponse,
  CasesListResponse,
  CreateCasePayload,
  CreateCaseResponse,
  CreditApplicationResponse,
  InvoiceDetailResponse,
  InvoiceListFilter,
  InvoicesListResponse,
  OrderDetail,
  OrderFulfillmentResponse,
  OrderListFilter,
  OrdersListResponse,
  PayLinkResponse,
  ProductSearchResponse,
  PublicIntakeResponse,
  PublicTradeReferenceResponse,
  QuoteDetailResponse,
  QuotesListResponse,
  PurchaseRequestDetail,
  PurchasingListResponse,
  ResendReferenceResponse,
  SubmitCreditApplicationPayload,
  SubmitCreditApplicationResponse,
  SubmitIntakePayload,
  SubmitIntakeResponse,
  SubmitPurchaseRequestPayload,
  SubmitPurchaseRequestResponse,
  SubmitTradeReferencePayload,
  SubmitTradeReferenceResponse,
} from '@/lib/api-types'

/**
 * The ONLY fetch path in the app. Every request goes through `request()`:
 * it attaches the bearer token, captures `X-Session-Refresh` rotations, and
 * funnels 401s into the single session-expired flow.
 */

// Default '' = same-origin: the api app serves both the SPA and /v1/*, so an
// unset env in a prod build does the right thing. Dev overrides live in
// .env.development / .env.local; vitest pins an absolute base (vitest.config).
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** `invalid_pack` only: 0-based index of the offending submitted line. */
  readonly lineIndex?: number

  constructor(status: number, code: string, message?: string, lineIndex?: number) {
    super(message ?? code)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.lineIndex = lineIndex
  }
}

const NETWORK_ERROR_MESSAGE = 'We couldn’t reach Green Line — check your connection.'

/** Signed-in sections worth returning to after a re-login. */
// '/purchasing' replaced the old purchase-request '/requests'; '/requests' now
// belongs to the Cases-backed Requests module.
const RETURNABLE_PREFIXES = [
  '/orders',
  '/purchasing',
  '/requests',
  '/quotes',
  '/invoices',
  '/account',
  '/credit',
]

function handleSessionExpired(): void {
  const { jwt, expireSession } = useSessionStore.getState()
  // Parallel queries can all 401 at once — only the first one gets to
  // clear the session and toast.
  if (!jwt) return
  const here = window.location.pathname + window.location.search
  if (RETURNABLE_PREFIXES.some((prefix) => here.startsWith(prefix))) {
    try {
      window.localStorage.setItem(RETURN_TO_KEY, here)
    } catch {
      // non-fatal
    }
  }
  // No navigation here — AuthGate owns the redirect. It sees the expired
  // flag and lands on /login?expired=1; a second navigation source would
  // race the gate's own <Navigate> and lose the explanation.
  expireSession()
  toast.error('Your session expired. Sign in again to keep going.')
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE'
  /** JSON body — sets Content-Type. (Multipart goes over xhrMultipart instead.) */
  json?: unknown
  /** false for the auth endpoints, where a 401 is a link problem, not an expired session. */
  auth?: boolean
  /** Caller cancellation (TanStack Query hands queries its own signal). */
  signal?: AbortSignal
  /** false disables the 30s guard (downloads can honestly run long). */
  timeout?: boolean
}

const REQUEST_TIMEOUT_MS = 30_000

/** Prefer combining; on browsers without AbortSignal.any, caller cancellation wins. */
function combineSignals(a?: AbortSignal, b?: AbortSignal): AbortSignal | undefined {
  if (a && b) return typeof AbortSignal.any === 'function' ? AbortSignal.any([a, b]) : a
  return a ?? b
}

async function rawRequest(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', json, auth = true, signal: callerSignal, timeout = true } = options

  const headers = new Headers()
  if (json !== undefined) headers.set('Content-Type', 'application/json')
  if (auth) {
    const jwt = useSessionStore.getState().jwt
    if (jwt) headers.set('Authorization', `Bearer ${jwt}`)
  }

  // A hung request must not spin forever — 30s guard on JSON traffic.
  // Downloads opt out via timeout: false; uploads live on the XHR path.
  const guard = timeout ? AbortSignal.timeout(REQUEST_TIMEOUT_MS) : undefined

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: json !== undefined ? JSON.stringify(json) : undefined,
      signal: combineSignals(callerSignal, guard),
    })
  } catch (error) {
    // Query cancellations pass through untouched (TanStack swallows its own
    // aborts); real failures become typed ApiErrors for the UI error states.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const timedOut = error instanceof DOMException && error.name === 'TimeoutError'
    throw new ApiError(
      0,
      timedOut ? 'timeout' : 'network_error',
      timedOut ? 'The request timed out.' : NETWORK_ERROR_MESSAGE,
    )
  }

  // Sliding sessions: the server may rotate the JWT on any response.
  const refreshedJwt = response.headers.get('X-Session-Refresh')
  if (refreshedJwt && useSessionStore.getState().jwt) {
    useSessionStore.getState().setJwt(refreshedJwt)
  }

  if (response.status === 401 && auth) {
    handleSessionExpired()
    throw new ApiError(401, 'unauthorized', 'Your session expired.')
  }

  if (!response.ok) {
    let code = 'request_failed'
    let message: string | undefined
    let lineIndex: number | undefined
    try {
      const body = (await response.json()) as ApiErrorBody
      if (body.error) code = body.error
      message = body.message
      if (typeof body.lineIndex === 'number') lineIndex = body.lineIndex
    } catch {
      // Non-JSON error body — keep the generic code.
    }
    throw new ApiError(response.status, code, message, lineIndex)
  }

  return response
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await rawRequest(path, options)
  return (await response.json()) as T
}

/** 0..1 upload fraction for multipart requests. */
export type UploadProgress = (fraction: number) => void

/**
 * fetch can't report upload progress, so multipart requests go over XHR with
 * identical semantics to rawRequest: bearer auth, X-Session-Refresh rotation,
 * ApiError mapping, and the shared 401 session-expired flow. No timeout —
 * uploads on slow links can honestly take minutes (same exemption as fetch).
 */
function xhrMultipart<T>(path: string, formData: FormData, onProgress?: UploadProgress): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE_URL}${path}`)
    const jwt = useSessionStore.getState().jwt
    if (jwt) xhr.setRequestHeader('Authorization', `Bearer ${jwt}`)

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total)
      }
    }

    xhr.onload = () => {
      const refreshed = xhr.getResponseHeader('X-Session-Refresh')
      if (refreshed && useSessionStore.getState().jwt) {
        useSessionStore.getState().setJwt(refreshed)
      }
      if (xhr.status === 401) {
        handleSessionExpired()
        reject(new ApiError(401, 'unauthorized', 'Your session expired.'))
        return
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        let code = 'request_failed'
        let message: string | undefined
        let lineIndex: number | undefined
        try {
          const body = JSON.parse(xhr.responseText) as ApiErrorBody
          if (body.error) code = body.error
          message = body.message
          if (typeof body.lineIndex === 'number') lineIndex = body.lineIndex
        } catch {
          // Non-JSON error body — keep the generic code.
        }
        reject(new ApiError(xhr.status, code, message, lineIndex))
        return
      }
      try {
        resolve(JSON.parse(xhr.responseText) as T)
      } catch {
        reject(new ApiError(xhr.status, 'bad_response', 'Unexpected response.'))
      }
    }
    xhr.onerror = () => reject(new ApiError(0, 'network_error', NETWORK_ERROR_MESSAGE))
    xhr.send(formData)
  })
}

/**
 * The purchase-request multipart body: one `payload` JSON field + exactly
 * one PO file part. Exported for direct testing.
 */
export function buildPurchaseRequestMultipart(
  payload: SubmitPurchaseRequestPayload,
  poFile: File,
): FormData {
  const formData = new FormData()
  formData.append('payload', JSON.stringify(payload))
  formData.append('poFile', poFile, poFile.name)
  return formData
}

/**
 * The case multipart body: one `payload` JSON field + up to MAX_CASE_FILES
 * file parts under `files`. Exported for direct testing.
 */
export function buildCaseMultipart(payload: CreateCasePayload, files: File[]): FormData {
  const formData = new FormData()
  formData.append('payload', JSON.stringify(payload))
  for (const file of files) formData.append('files', file, file.name)
  return formData
}

export const api = {
  authStart(email: string): Promise<AuthStartResponse> {
    return request('/v1/client/auth/start', { method: 'POST', json: { email }, auth: false })
  },

  authComplete(token: string): Promise<AuthCompleteResponse> {
    return request('/v1/client/auth/complete', { method: 'POST', json: { token }, auth: false })
  },

  // --- Orders ---
  listOrders(status: OrderListFilter, init?: { signal?: AbortSignal }): Promise<OrdersListResponse> {
    return request(`/v1/client/orders?status=${status}`, { signal: init?.signal })
  },

  getOrder(id: string, init?: { signal?: AbortSignal }): Promise<OrderDetail> {
    return request(`/v1/client/orders/${encodeURIComponent(id)}`, { signal: init?.signal })
  },

  getOrderFulfillment(id: string, init?: { signal?: AbortSignal }): Promise<OrderFulfillmentResponse> {
    return request(`/v1/client/orders/${encodeURIComponent(id)}/fulfillment`, { signal: init?.signal })
  },

  // --- Purchasing (purchase order requests) ---
  listPurchaseRequests(init?: { signal?: AbortSignal }): Promise<PurchasingListResponse> {
    return request('/v1/client/purchasing', { signal: init?.signal })
  },

  getPurchaseRequest(id: string, init?: { signal?: AbortSignal }): Promise<PurchaseRequestDetail> {
    return request(`/v1/client/purchasing/${encodeURIComponent(id)}`, { signal: init?.signal })
  },

  submitPurchaseRequest(
    payload: SubmitPurchaseRequestPayload,
    poFile: File,
    onProgress?: UploadProgress,
  ): Promise<SubmitPurchaseRequestResponse> {
    return xhrMultipart(
      '/v1/client/purchasing',
      buildPurchaseRequestMultipart(payload, poFile),
      onProgress,
    )
  },

  // --- Requests (Case-backed customer service) ---
  listCases(
    scope: CaseListScope,
    status: CaseListFilter,
    init?: { signal?: AbortSignal },
  ): Promise<CasesListResponse> {
    return request(`/v1/client/cases?scope=${scope}&status=${status}`, { signal: init?.signal })
  },

  getCase(id: string, init?: { signal?: AbortSignal }): Promise<CaseDetail> {
    return request(`/v1/client/cases/${encodeURIComponent(id)}`, { signal: init?.signal })
  },

  createCase(
    payload: CreateCasePayload,
    files: File[],
    onProgress?: UploadProgress,
  ): Promise<CreateCaseResponse> {
    return xhrMultipart('/v1/client/cases', buildCaseMultipart(payload, files), onProgress)
  },

  /** Returns the canonical timeline entry the server minted for the comment. */
  postCaseComment(caseId: string, body: string): Promise<CaseCommentEntry> {
    return request(`/v1/client/cases/${encodeURIComponent(caseId)}/comments`, {
      method: 'POST',
      json: { body },
    })
  },

  addCaseParticipant(caseId: string, contactId: string): Promise<CaseParticipantsResponse> {
    return request(`/v1/client/cases/${encodeURIComponent(caseId)}/participants`, {
      method: 'POST',
      json: { contactId },
    })
  },

  removeCaseParticipant(caseId: string, contactId: string): Promise<CaseParticipantsResponse> {
    return request(
      `/v1/client/cases/${encodeURIComponent(caseId)}/participants/${encodeURIComponent(contactId)}`,
      { method: 'DELETE' },
    )
  },

  uploadCaseFiles(
    caseId: string,
    files: File[],
    onProgress?: UploadProgress,
  ): Promise<CaseFileUploadResponse> {
    const formData = new FormData()
    for (const file of files) formData.append('files', file, file.name)
    return xhrMultipart(`/v1/client/cases/${encodeURIComponent(caseId)}/files`, formData, onProgress)
  },

  async downloadCaseFile(caseId: string, docId: string): Promise<Blob> {
    // No timeout: attachment streams can honestly run long (same exemption
    // as the quote PDF).
    const response = await rawRequest(
      `/v1/client/cases/${encodeURIComponent(caseId)}/files/${encodeURIComponent(docId)}`,
      { timeout: false },
    )
    return response.blob()
  },

  // --- Quotes ---
  listQuotes(init?: { signal?: AbortSignal }): Promise<QuotesListResponse> {
    return request('/v1/client/quotes', { signal: init?.signal })
  },

  getQuote(id: string, init?: { signal?: AbortSignal }): Promise<QuoteDetailResponse> {
    return request(`/v1/client/quotes/${encodeURIComponent(id)}`, { signal: init?.signal })
  },

  async downloadQuotePdf(id: string): Promise<Blob> {
    // No timeout: the org renders the PDF on demand — it can honestly run long.
    const response = await rawRequest(`/v1/client/quotes/${encodeURIComponent(id)}/pdf`, {
      timeout: false,
    })
    return response.blob()
  },

  // --- Invoices ---
  listInvoices(
    status: InvoiceListFilter,
    init?: { signal?: AbortSignal },
  ): Promise<InvoicesListResponse> {
    return request(`/v1/client/invoices?status=${status}`, { signal: init?.signal })
  },

  getInvoice(id: string, init?: { signal?: AbortSignal }): Promise<InvoiceDetailResponse> {
    return request(`/v1/client/invoices/${encodeURIComponent(id)}`, { signal: init?.signal })
  },

  createPayLink(invoiceId: string): Promise<PayLinkResponse> {
    return request(`/v1/client/invoices/${encodeURIComponent(invoiceId)}/pay-link`, {
      method: 'POST',
    })
  },

  // --- Product catalog (wizard) ---
  searchProducts(term: string, init?: { signal?: AbortSignal }): Promise<ProductSearchResponse> {
    return request(`/v1/client/products/search?q=${encodeURIComponent(term)}`, {
      signal: init?.signal,
    })
  },

  // --- Account ---
  getAccount(init?: { signal?: AbortSignal }): Promise<AccountResponse> {
    return request('/v1/client/account', { signal: init?.signal })
  },

  // --- Credit application ---
  getCreditApplication(init?: { signal?: AbortSignal }): Promise<CreditApplicationResponse> {
    return request('/v1/client/credit-application', { signal: init?.signal })
  },

  submitCreditApplication(
    payload: SubmitCreditApplicationPayload,
  ): Promise<SubmitCreditApplicationResponse> {
    return request('/v1/client/credit-application', { method: 'POST', json: payload })
  },

  resendReference(refId: string): Promise<ResendReferenceResponse> {
    return request(`/v1/client/credit-application/references/${encodeURIComponent(refId)}/resend`, {
      method: 'POST',
    })
  },

  // --- Public trade-reference form (tokenized link — no session) ---
  publicGetTradeReference(
    token: string,
    init?: { signal?: AbortSignal },
  ): Promise<PublicTradeReferenceResponse> {
    return request(`/v1/public/trade-reference/${encodeURIComponent(token)}`, {
      auth: false,
      signal: init?.signal,
    })
  },

  publicSubmitTradeReference(
    token: string,
    body: SubmitTradeReferencePayload,
  ): Promise<SubmitTradeReferenceResponse> {
    return request(`/v1/public/trade-reference/${encodeURIComponent(token)}`, {
      method: 'POST',
      json: body,
      auth: false,
    })
  },

  // --- Public customer-intake form (tokenized link — no session) ---
  publicGetIntake(token: string, init?: { signal?: AbortSignal }): Promise<PublicIntakeResponse> {
    return request(`/v1/public/intake/${encodeURIComponent(token)}`, {
      auth: false,
      signal: init?.signal,
    })
  },

  publicSubmitIntake(token: string, body: SubmitIntakePayload): Promise<SubmitIntakeResponse> {
    return request(`/v1/public/intake/${encodeURIComponent(token)}`, {
      method: 'POST',
      json: body,
      auth: false,
    })
  },
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/** Fetch the quote PDF and hand it to the browser as a download. */
export async function saveQuotePdf(quoteId: string, filename: string): Promise<void> {
  triggerBlobDownload(await api.downloadQuotePdf(quoteId), filename)
}

/** Fetch a case attachment and hand it to the browser as a download. */
export async function saveCaseFile(caseId: string, docId: string, filename: string): Promise<void> {
  triggerBlobDownload(await api.downloadCaseFile(caseId, docId), filename)
}
